import { createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { User } from '@/lib/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, twoFactorToken?: string) => Promise<any>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  requireAuth: () => boolean;
  canAccess: (requiredTier: string) => boolean;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  age?: number;
  tier?: string;
  learningStyle?: string;
  preferredComplexity?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const publicRoutePrefixes = [
    '/learn/',
    '/track/',
    '/course/',
  ];
  const publicRoutes = new Set([
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/courses',
    '/starter-kits',
    '/playground',
    '/career',
    '/certifications',
    '/roadmap',
    '/documentation',
    '/community',
    '/blog',
    '/research',
    '/reports',
    '/career-guide',
    '/ai-careers',
    '/ai-test',
    '/help',
    '/contact',
    '/support',
    '/accessibility',
    '/privacy',
    '/terms',
    '/payment-plans',
  ]);
  const isPublicRoute = publicRoutes.has(location) ||
    publicRoutePrefixes.some((prefix) => location.startsWith(prefix));

  const authQueryEnabled = !isPublicRoute;

  // Get current user - handle 401 and 2FA requirements gracefully
  const { data: response, isLoading: authQueryLoading, error } = useQuery({
    queryKey: ['/api/me'],
    enabled: authQueryEnabled,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for auth errors
      return failureCount < 2 && !error?.message?.includes('401') && !error?.message?.includes('403');
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    queryFn: async () => {
      try {
        const res = await fetch('/api/me', { 
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (res.status === 401) {
          return { user: null }; // Not authenticated, return null user
        }
        
        if (res.status === 403) {
          const errorData = await res.json();
          if (errorData.requiresTwoFactorSetup || errorData.requires2FASetup) {
            return { 
              user: null, 
              requiresTwoFactorSetup: true,
              message: errorData.message 
            };
          }
        }
        
        if (!res.ok) {
          throw new Error(`${res.status}: ${res.statusText}`);
        }
        
        const data = await res.json();
        return data;
      } catch (err) {
        console.error('Auth query error:', err);
        // Only return null user for actual auth failures, not network errors
        if (err instanceof TypeError && err.message.includes('fetch')) {
          throw err; // Let React Query handle network errors with retry
        }
        return { user: null };
      }
    },
  });

  // Extract user from response object
  const user = response?.user;



  const isAuthenticated = !!user && !error;

  // Login mutation with 2FA support
  const loginMutation = useMutation({
    mutationFn: async ({ email, password, twoFactorToken }: { email: string; password: string; twoFactorToken?: string }) => {
      const response = await apiRequest('POST', '/api/login', { email, password, twoFactorToken });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.user) {
        queryClient.setQueryData(['/api/me'], data);
        queryClient.invalidateQueries({ queryKey: ['/api/me'] });
        toast({
          title: "Welcome back!",
          description: "You have been logged in successfully.",
        });
        setLocation('/dashboard');
      }
      // If no user data, it means we need 2FA token (handled by component)
    },
    onError: (error: any) => {
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterData) => {
      const response = await apiRequest('POST', '/api/register', userData);
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Welcome to AI Academy!",
        description: "Your account has been created successfully. Please log in.",
      });
      setLocation('/login');
    },
    onError: (error: any) => {
      toast({
        title: "Registration failed",
        description: error.message || "Could not create account",
        variant: "destructive",
      });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest('POST', '/api/logout', {});
    },
    onSuccess: () => {
      queryClient.setQueryData(['/api/me'], null);
      queryClient.clear();
      toast({
        title: "Logged out",
        description: "You have been logged out successfully.",
      });
      setLocation('/');
    },
    onError: (error: any) => {
      toast({
        title: "Logout failed",
        description: error.message || "Could not log out",
        variant: "destructive",
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (updates: Partial<User>) => {
      const response = await apiRequest('PUT', '/api/me', updates);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['/api/me'], data.user);
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Could not update profile",
        variant: "destructive",
      });
    },
  });

  const login = async (email: string, password: string, twoFactorToken?: string) => {
    return loginMutation.mutateAsync({ email, password, twoFactorToken });
  };

  const register = async (userData: RegisterData) => {
    return registerMutation.mutateAsync(userData);
  };

  const logout = async () => {
    return logoutMutation.mutateAsync(undefined as any);
  };

  const updateUser = async (updates: Partial<User>) => {
    return updateUserMutation.mutateAsync(updates);
  };

  const isAuthLoading = (authQueryEnabled && authQueryLoading) ||
    loginMutation.isPending ||
    registerMutation.isPending ||
    logoutMutation.isPending;

  const requireAuth = (): boolean => {
    if (!isAuthLoading && !isAuthenticated) {
      // Don't show toast on auth pages (login, register, etc.)
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];
      
      if (!authPages.includes(currentPath)) {
        toast({
          title: "Authentication required",
          description: "Please log in to access this page.",
          variant: "destructive",
        });
      }
      
      setLocation('/login');
      return false;
    }
    return isAuthenticated;
  };

  const canAccess = (requiredTier: string): boolean => {
    // Guest content is always accessible
    if (requiredTier === 'guest') {
      return true;
    }
    
    // No user logged in - can only access guest content
    if (!user) {
      return false;
    }
    
    // Admin users have access to everything
    if (user.tier === 'admin') {
      return true;
    }
    
    const tierHierarchy: Record<string, number> = {
      guest: 0,
      student: 1,
      professional: 2,
      enterprise: 3,
      admin: 4
    };
    
    // Map 'paid' tier to professional level.
    const normalizedRequiredTier = requiredTier === 'paid' ? 'professional' : requiredTier;
    
    const userTierLevel = tierHierarchy[user.tier] || 0;
    const requiredTierLevel = tierHierarchy[normalizedRequiredTier] || 0;
    
    return userTierLevel >= requiredTierLevel;
  };

  const value: AuthContextType = {
    user: user || null,
    isLoading: isAuthLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    requireAuth,
    canAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
